import React, { useEffect, useState } from 'react';
import { Button, Card, CardFooter, Col, Row } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import * as Icon from 'react-feather';
import { useFormik } from 'formik';

import FormInput from '../../../components/form/FormInput';
import CallexLoader from '../../../components/CallexLoader';
import FormSelect from '../../../components/form/FormSelect';
import Pagination from '../../../components/form/Pagination';
import AdminHeader from '../../../components/header/AdminHeader';
import AddEditSubDispositionModal from './AddEditSubDispositionModal';
import DeleteConfirmationModal from '../../../components/Modals/DeleteConfirmationModal';

import {
  dispositionListGetByProcess,
  subDispositionDelete,
  subDispositionListGet,
  useSubDispositionList
} from '../../../store/slices/tenantSlices/settingSlice';
import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import { useUserRolePermission } from '../../../store/slices/authSlice';
import { checkModulePermission } from '../../../utils/common';

const SubDisposition = () => {
  const dispatch = useDispatch();
  const subDispositionList = useSubDispositionList();
  const userRolePermission = useUserRolePermission();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [processOptions, setProcessOptions] = useState([]);
  const [dispOptions, setDispOptions] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [isEdit, setIsEdit] = useState(null);
  const [isDeleteId, setIsDeleteId] = useState(null);

  const initialValues = {
    page: 1,
    limit: 10,
    search: '',
    process_id: '',
    disposition_id: ''
  };

  const handleFormSubmit = async (reset = false) => {
    setIsLoading(true);
    setIsFilterOpen(false);
    try {
      const payload = { ...values };
      if (reset === true) {
        payload['disposition_id'] = '';
        payload['process_id'] = '';
        payload['search'] = '';
      }
      await dispatch(subDispositionListGet(payload)).unwrap();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log('error', error);
    }
  };

  const { values, handleChange, handleBlur, setFieldValue, handleSubmit, touched, errors } =
    useFormik({
      initialValues,
      onSubmit: handleFormSubmit
    });

  useEffect(() => {
    handleFormSubmit();
  }, [values?.limit, values?.page]);

  const getDispOption = async () => {
    try {
      const response = await dispatch(
        dispositionListGetByProcess({ process_id: values?.process_id })
      ).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.DisposCode,
          value: val.id
        }));
        setDispOptions(options);
      } else {
        setDispOptions([]);
      }
    } catch (error) {
      setDispOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (values?.process_id !== '') {
      getDispOption();
    } else {
      setDispOptions([]);
    }
  }, [values?.process_id]);

  const getProcessOption = async (id) => {
    try {
      const response = await dispatch(processAllOptionGet({ crm_id: id })).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  const deleteDispo = async () => {
    try {
      const response = await dispatch(subDispositionDelete(isDeleteId)).unwrap();
      if (response) {
        toast.success('Record deleted successfully');
        handleFormSubmit();
        setIsDeleteId(null);
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  return (
    <div>
      <AdminHeader
        title="Sub Disposition"
        isSearch
        isAddDisabled={!checkModulePermission('is_add', 'Sub Disposition', userRolePermission)}
        onSearchClick={() => setIsFilterOpen(!isFilterOpen)}
        isAdd
        addBtnName="Add Sub Disposition"
        onAddClick={() => setOpenAddModal(true)}
      />
      {isFilterOpen && (
        <Card className="mb-3 p-3">
          <form onSubmit={handleSubmit}>
            <Row>
              <Col lg="3" md="3" className="flex-row">
                <FormInput
                  name="search"
                  label="Search"
                  placeholder="Search here"
                  value={values.search}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Col>
              <Col lg="3" md="2" className="flex-row">
                <FormSelect
                  label="Process"
                  placeholder="Select Process"
                  value={values.process_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="process_id"
                  options={processOptions}
                  touched={touched}
                  errors={errors}
                  menuPlacement="bottom"
                />
              </Col>
              <Col lg="3" md="2" className="flex-row">
                <FormSelect
                  label="Disposition"
                  placeholder="Select Disposition"
                  value={values.disposition_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  name="disposition_id"
                  options={dispOptions}
                  touched={touched}
                  errors={errors}
                  menuPlacement="bottom"
                />
              </Col>
            </Row>
            <CardFooter className="p-0 bg-white text-end">
              <Button
                className="px-4 me-4 bg-transparent text-primary"
                onClick={() => {
                  handleFormSubmit(true);
                  setFieldValue('search', '');
                  setFieldValue('process_id', '');
                  setFieldValue('disposition_id', '');
                }}>
                Clear
              </Button>
              <Button color="primary" className="px-4" type="submit">
                Search
              </Button>
            </CardFooter>
          </form>
        </Card>
      )}
      {isLoading ? (
        <Card
          className="p-0 d-flex justify-content-center align-items-center m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          <CallexLoader />
        </Card>
      ) : subDispositionList?.data?.length > 0 &&
        checkModulePermission('is_view', 'Sub Disposition', userRolePermission) ? (
        <Card className="p-0 position-relative">
          <div
            className="daily-report-table"
            style={{ height: isFilterOpen ? 'calc(100vh - 369px)' : 'calc(100vh - 208px)' }}>
            <table className="report-list-table">
              <thead>
                <tr>
                  <th>Sub Disposition Code</th>
                  <th>Disposition Code</th>
                  <th>Process Name</th>
                  <th>Active</th>
                  <th className="text-center" style={{ width: '90px' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {subDispositionList?.data?.map((val, index) => {
                  return (
                    <tr key={index}>
                      <td>{val?.SubDisposCode}</td>
                      <td>{val?.DispoCode || '-'}</td>
                      <td>{val?.ProcessName}</td>
                      <td>{String(val?.active)}</td>
                      <td>
                        <div className="d-flex justify-content-around align-items-center">
                          <div
                            className={`cursor-pointer text-info ${
                              !checkModulePermission(
                                'is_edit',
                                'Sub Disposition',
                                userRolePermission
                              )
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Edit
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_edit',
                                    'Sub Disposition',
                                    userRolePermission
                                  )
                                ) {
                                  setEditRecord(val);
                                  setIsEdit(true);
                                  setOpenAddModal(true);
                                }
                              }}
                            />
                          </div>
                          <div
                            className={`cursor-pointer text-danger ${
                              !checkModulePermission(
                                'is_delete',
                                'Sub Disposition',
                                userRolePermission
                              )
                                ? 'callex-disabled'
                                : ''
                            }`}>
                            <Icon.Trash
                              size={16}
                              onClick={() => {
                                if (
                                  checkModulePermission(
                                    'is_delete',
                                    'Sub Disposition',
                                    userRolePermission
                                  )
                                ) {
                                  setIsDeleteId(val?.id);
                                }
                              }}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="position-absolute w-100" style={{ bottom: '-56px' }}>
            <Pagination
              totalCount={subDispositionList?.total}
              limit={subDispositionList?.limit}
              page={subDispositionList?.page}
              onLimitChange={(val) => {
                setFieldValue('limit', val);
                setFieldValue('page', 1);
              }}
              onPageClick={(val) => setFieldValue('page', val)}
              lastPageClick={(val) => setFieldValue('page', val)}
              firstPageClick={(val) => setFieldValue('page', val)}
              onRefreshClick={() => handleFormSubmit()}
            />
          </div>
        </Card>
      ) : (
        <Card
          className="p-0 d-flex justify-content-center align-items-center no-data m-0"
          style={{ height: isFilterOpen ? 'calc(100vh - 328px)' : 'calc(100vh - 165px)' }}>
          No data available
        </Card>
      )}
      <DeleteConfirmationModal
        isOpen={!!isDeleteId}
        onCloseClick={() => setIsDeleteId(null)}
        onDeleteClick={() => deleteDispo()}
      />
      <AddEditSubDispositionModal
        isOpen={openAddModal}
        onCloseClick={(apiCall = false) => {
          setOpenAddModal(false);
          setEditRecord(null);
          setIsEdit(false);
          if (apiCall) {
            handleFormSubmit();
          }
        }}
        editRecord={editRecord}
        isEdit={isEdit}
      />
    </div>
  );
};

export default SubDisposition;
